import AddCardModal from "../../Profile/AddCardModal/AddCardModal";
import styles from "./UpgradeCardSelector.module.scss";
import { IoIosAddCircleOutline } from "react-icons/io";
import { RootState } from "../../../../Redux/store";
import React, { useMemo, useState } from "react";
import { RiCoupon2Line } from "react-icons/ri";
import { PiCarDuotone } from "react-icons/pi";
import { useSelector } from "react-redux";
import API from "../../../../API/API";
import axios from "axios";
import {
  getPackagePrice,
  getPackageTrialDays,
  getPeriodLabel,
  getPeriodSuffix,
  hasPackageFreeTrial,
} from "../../../../Util/packageRates";

interface Props {
  selectedCard: string;
  setSelectedCard: React.Dispatch<React.SetStateAction<string>>;
  label: string;
  voucher: string;
  setVoucher: React.Dispatch<React.SetStateAction<string>>;
  voucherSuccess: any;
  setVoucherSuccess: React.Dispatch<React.SetStateAction<any>>;
  selectedPackage: any;
  selectedPeriod?: string;
  selectedParkingIds: number[];
  selectedAddons: Array<{
    addonId: number;
    noofAddonUnits: number;
    notifyAddon: boolean;
  }>;
  parkingList: any[];
  addonsList: any[];
}

function UpgradeCardSelector(props: Props) {
  const {
    label,
    voucher,
    setVoucher,
    selectedCard,
    setSelectedCard,
    voucherSuccess,
    setVoucherSuccess,
    selectedPackage,
    selectedPeriod,
    selectedParkingIds,
    selectedAddons,
    parkingList,
    addonsList,
  } = props;

  const { profileData } = useSelector((state: RootState) => state.profile);

  const [addNewCardModalOpen, setAddNewCardModalOpen] = useState(false);
  const [voucherError, setVoucherError] = useState<string>("");

  const applyVoucherHandler = async () => {
    if (voucher?.trim() !== "") {
      setVoucherError("");
      try {
        const { data } = await axios({
          url: `${API.members.validateVoucher.url}?voucherCode=${voucher}&email=${profileData?.email}&voucherType=1`,
          method: API.members.validateVoucher.method,
        });
        setVoucherSuccess(data);
      } catch (error: any) {
        if (error?.response?.data?.message) {
          setVoucherError(error?.response?.data?.message ?? "");
        } else {
          setVoucherError("Something went wrong");
        }
      }
    } else {
      setVoucherError("Voucher code is required");
    }
  };

  const removeVoucherHandler = () => {
    setVoucherSuccess(null);
    setVoucher("");
  };

  const subTotal = useMemo(() => {
    let totalOrderrate = 0;
    if (selectedPackage) {
      totalOrderrate += getPackagePrice(selectedPackage, selectedPeriod);
    }
    if (selectedParkingIds.length > 0) {
      let totalParkingRate = 0;
      parkingList.forEach((parking: any) => {
        const slotIds = (parking?.parkingdata ?? []).map(
          (slot: { ParkingId: number }) => slot.ParkingId,
        );
        const selectedCount = selectedParkingIds.filter((id) =>
          slotIds.includes(id),
        ).length;
        totalParkingRate +=
          (parking?.rates ?? parking?.cost ?? 0) * selectedCount;
      });
      totalOrderrate += totalParkingRate;
    }
    if (selectedAddons.some((s) => s.noofAddonUnits > 0)) {
      let totalAddonRate = 0;
      selectedAddons.forEach((s) => {
        const addon = addonsList.find((a: any) => a.addonId === s.addonId);
        totalAddonRate += (addon?.cost ?? 0) * s.noofAddonUnits;
      });
      totalOrderrate += totalAddonRate;
    }

    return Number(totalOrderrate.toFixed(2));
  }, [
    selectedPackage,
    selectedPeriod,
    selectedParkingIds,
    selectedAddons,
    parkingList,
    addonsList,
  ]);

  const voucherDiscountAmount = useMemo(() => {
    if (!voucherSuccess?.success || !voucherSuccess?.voucher) return 0;

    const { percent, maxDiscount } = voucherSuccess.voucher;
    let discount = (subTotal * Number(percent ?? 0)) / 100;
    if (maxDiscount !== null && maxDiscount !== undefined) {
      discount = Math.min(discount, Number(maxDiscount));
    }
    return Number(discount.toFixed(2));
  }, [voucherSuccess, subTotal]);

  return (
    <React.Fragment>
      <AddCardModal
        isOpen={addNewCardModalOpen}
        setIsOpen={setAddNewCardModalOpen}
      />
      <div className={styles.mainContainer}>
        <div className={styles.headingContainer}>
          <p className={styles.label}>{label}</p>
        </div>

        <div className={styles.mainSrollContainer}>
          <div className={styles.orderSummaryContainer}>
            {selectedPackage && (
              <div className={styles.packageContainer}>
                <p className={styles.label}>Package Selected</p>

                <p className={styles.packageName}>
                  {selectedPackage?.packageName}
                  {selectedPeriod
                    ? ` (${getPeriodLabel(selectedPeriod)})`
                    : ""}{" "}
                  - $
                  {getPackagePrice(selectedPackage, selectedPeriod)}
                  {getPeriodSuffix(selectedPeriod)}
                </p>
                {hasPackageFreeTrial(selectedPackage) && (
                  <p className={styles.trialNotice}>
                    You have a {getPackageTrialDays(selectedPackage)}-day free
                    trial. You will be charged after the trial ends.
                  </p>
                )}
              </div>
            )}
            {selectedParkingIds.length > 0 && (
              <div className={styles.amenityMainContainer}>
                <div className={styles.iconContainer}>
                  <PiCarDuotone className={styles.icon} />
                </div>
                <div className={styles.amenitiesContainer}>
                  <p className={styles.label}>Parking Selected</p>
                  <div className={styles.amenitiesList}>
                    {parkingList.map((parking: any) => {
                      const slotIds = (parking?.parkingdata ?? []).map(
                        (slot: { ParkingId: number }) => slot.ParkingId,
                      );
                      const selectedCount = selectedParkingIds.filter((id) =>
                        slotIds.includes(id),
                      ).length;

                      if (selectedCount === 0) return null;

                      return (
                        <div key={parking?.amenityId} className={styles.item}>
                          <p className={styles.label}>
                            {parking?.name ?? parking?.amenityName} (
                            {selectedCount})
                          </p>

                          <p className={styles.price}>
                            $
                            {(parking?.rates ?? parking?.cost ?? 0) *
                              selectedCount}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            {selectedAddons.some((s) => s.noofAddonUnits > 0) && (
              <div className={styles.amenityMainContainer}>
                <div className={styles.iconContainer}>
                  <IoIosAddCircleOutline className={styles.icon} />
                </div>
                <div className={styles.amenitiesContainer}>
                  <p className={styles.label}>Addons Selected</p>
                  <div className={styles.amenitiesList}>
                    {selectedAddons.map((selection, i) => {
                      const addon = addonsList.find(
                        (a: any) => a.addonId === selection.addonId,
                      );

                      if (!addon || selection.noofAddonUnits <= 0) return null;

                      return (
                        <div key={i} className={styles.item}>
                          <p className={styles.label}>{addon?.amenityName}</p>

                          <p className={styles.price}>
                            ${(addon?.cost ?? 0) * selection.noofAddonUnits}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          {!voucherSuccess ? (
            <div className={styles.couponContainer}>
              <div className={styles.labelContainer}>
                <p className={styles.label}>Voucher Code</p>
              </div>
              <div className={styles.couponInputContainer}>
                <div className={styles.couponInputBox}>
                  <div className={styles.iconContainer}>
                    <RiCoupon2Line className={styles.icon} />
                  </div>
                  <input
                    className={styles.input}
                    type="text"
                    placeholder="Enter Voucher Code"
                    value={voucher}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\s/g, "");
                      setVoucher(value);
                    }}
                  />
                </div>
                <button className={styles.button} onClick={applyVoucherHandler}>
                  Apply
                </button>
              </div>
              <p className={styles.error}>{voucherError}</p>
            </div>
          ) : (
            <div className={styles.couponSuccessContainer}>
              <p className={styles.heading}>Coupon Applied</p>
              <div className={styles.voucherContainer}>
                <p className={styles.voucherCode}>{voucher}</p>
                <p className={styles.voucherDiscount}>
                  -${voucherDiscountAmount}
                </p>
              </div>
              <div className={styles.buttonContainer}>
                <button
                  className={styles.button}
                  onClick={removeVoucherHandler}
                >
                  Remove Voucher
                </button>
              </div>
            </div>
          )}
          <div className={styles.summaryContainer}>
            <div className={styles.totalContainer}>
              <div className={styles.itemContainer}>
                <p className={styles.label}>Subtotal</p>
                <p className={styles.value}>$ {subTotal}</p>
              </div>
              <div className={styles.discountItemContainer}>
                <p className={styles.label}>Discount</p>
                <p className={styles.value}>$ {voucherDiscountAmount}</p>
              </div>
              <div className={styles.itemContainer}>
                <p className={styles.label}>Taxes and Fees</p>
                <p className={styles.value}>$0.00</p>
              </div>
              <div className={styles.totalItemContainer}>
                <p className={styles.label}>Total</p>
                <p className={styles.value}>
                  $ {subTotal - voucherDiscountAmount}
                </p>
              </div>
            </div>
          </div>
          <div className={styles.selectCardBodyContainer}>
            <div className={styles.inputContainer}>
              <p className={styles.label}>Select Card</p>
              <select
                className={styles.input}
                value={selectedCard}
                onChange={(e) => setSelectedCard(e.target.value)}
              >
                {!selectedCard && <option value="">Select Card</option>}
                {profileData?.memberCCInfo?.map((card: any, i: number) => (
                  <option key={i} value={card?.id}>
                    XXX - {card?.cardNumber}
                  </option>
                ))}
              </select>
            </div>
            <div
              className={styles.buttonContainer}
              onClick={() => setAddNewCardModalOpen(true)}
            >
              <div className={styles.iconContainer}>
                <IoIosAddCircleOutline />
              </div>
              <button className={styles.button}>Add new card</button>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

export default UpgradeCardSelector;
